import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { X, Truck, DollarSign, MapPin, Save, Loader2, CheckCircle2, Circle } from 'lucide-react';
import { cn, formatFinalizedDate } from '@/lib/utils';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { DQF_ITEMS } from './DQFChecklist';

const dqfDaysUntil = (d) => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;

const ExpiryStat = ({ label, date }) => {
  const days = dqfDaysUntil(date);
  const cls = days === null ? 'text-muted-foreground'
    : days <= 0 ? 'text-red-400' : days <= 14 ? 'text-orange-400' : days <= 30 ? 'text-amber-400' : 'text-foreground';
  return (
    <div className="glass-card-dark rounded-xl p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={cn("text-sm font-semibold mt-0.5", cls)}>
        {date || '—'}{days !== null && (days <= 0 ? ' · EXPIRED' : days <= 30 ? ` · ${days}d` : '')}
      </p>
    </div>
  );
};

const statusColors = {
  Available: 'text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30',
  Booked: 'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30',
  'In Transit': 'text-blue-700 dark:text-blue-400 bg-blue-500/15 dark:bg-blue-500/10 border-blue-500/40 dark:border-blue-500/30',
  Delivered: 'text-purple-700 dark:text-purple-400 bg-purple-500/15 dark:bg-purple-500/10 border-purple-500/40 dark:border-purple-500/30',
};

const settlementStatusColors = {
  Pending: 'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30',
  Approved: 'text-blue-700 dark:text-blue-400 bg-blue-500/15 dark:bg-blue-500/10 border-blue-500/40 dark:border-blue-500/30',
  Finalized: 'text-purple-700 dark:text-purple-400 bg-purple-500/15 dark:bg-purple-500/10 border-purple-500/40 dark:border-purple-500/30',
  Paid: 'text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30',
};

export default function DriverDetailModal({ driver, onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [loads, setLoads] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('loads');
  const [truckPayment, setTruckPayment] = useState(driver?.weekly_truck_payment ?? 0);
  const [trailerPayment, setTrailerPayment] = useState(driver?.weekly_trailer_payment ?? 0);
  const [savingPayments, setSavingPayments] = useState(false);

  useEffect(() => {
    if (!driver?.user_email) { setLoading(false); return; }
    const email = driver.user_email.toLowerCase();
    Promise.all([
      client.entities.Load.filter({ assigned_driver_email: email }, '-created_date', 50),
      client.entities.Settlement.filter({ driver_email: email }, '-created_date', 50),
    ]).then(([ls, ss]) => {
      setLoads(ls);
      setSettlements(ss);
      setLoading(false);
    });
  }, [driver?.user_email]);

  // Use stored YTD values from profile (updated when settlements are finalized)
  const totalGross = driver.ytd_gross_earnings || settlements.filter(s => s.is_finalized).reduce((s, x) => s + (x.gross_revenue || 0), 0);
  const totalNet = driver.ytd_net_earnings || settlements.filter(s => s.is_finalized).reduce((s, x) => s + (x.final_net_pay ?? x.net_driver_pay ?? 0), 0);
  const dqfDone = DQF_ITEMS.filter(i => driver[i.key]).length;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-2xl my-8 z-10 overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-border">
          <div>
            <h2 className="font-space font-bold text-foreground text-lg">{driver.full_name}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {driver.user_email}{driver.truck_type ? ` · ${driver.truck_type}` : ''}
            </p>
            {driver.phone && <p className="text-xs text-muted-foreground">{driver.phone}</p>}
            {driver.mc_number && <p className="text-xs text-muted-foreground">MC: {driver.mc_number} {driver.dot_number && `· DOT: ${driver.dot_number}`}</p>}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground mt-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* YTD Stats */}
        <div className="grid grid-cols-2 gap-4 px-6 py-4 border-b border-border">
          <div className="glass-card-dark rounded-xl p-3 text-center">
            <p className="text-xs text-muted-foreground">YTD Gross</p>
            <p className="text-lg font-bold text-foreground">${totalGross.toLocaleString()}</p>
          </div>
          <div className="glass-card-dark rounded-xl p-3 text-center">
            <p className="text-xs text-muted-foreground">YTD Net</p>
            <p className="text-lg font-bold text-green-400">${totalNet.toLocaleString()}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {['loads', 'payments', 'dqf'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                "flex-1 py-3 text-sm font-medium transition-colors",
                tab === t ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {t === 'loads' ? `Loads (${loads.length})` : t === 'payments' ? `Payment History (${settlements.length})` : `DQF (${dqfDone}/${DQF_ITEMS.length})`}
            </button>
          ))}
        </div>

        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-8 flex justify-center">
              <div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
            </div>
          ) : tab === 'loads' ? (
            loads.length === 0 ? (
              <p className="text-center text-muted-foreground py-8 text-sm">No loads assigned yet.</p>
            ) : (
              <div className="divide-y divide-border">
                {loads.map(l => (
                  <div key={l.id} className="flex items-center justify-between px-6 py-3 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Truck className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">#{l.load_number} — {l.origin} → {l.destination}</p>
                        <p className="text-xs text-muted-foreground">{l.equipment_required} · {l.pickup_date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-sm font-semibold text-primary">${l.rate?.toLocaleString()}</span>
                      <span className={cn("text-xs font-semibold px-2 py-0.5 rounded-full border", statusColors[l.status])}>{l.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : tab === 'payments' ? (
            settlements.length === 0 ? (
              <p className="text-center text-muted-foreground py-8 text-sm">No payment history yet.</p>
            ) : (
              <div className="divide-y divide-border">
                {settlements.map(s => (
                  <div key={s.id} className="flex items-center justify-between px-6 py-3 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <DollarSign className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-foreground">Load #{s.load_number}</p>
                        <p className="text-xs text-muted-foreground">{s.settlement_date || formatFinalizedDate(s.finalized_date)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right">
                        <p className="text-sm font-bold text-green-400">${(s.final_net_pay ?? s.net_driver_pay ?? 0).toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground">net</p>
                      </div>
                      <span className={cn("text-xs font-semibold px-2 py-0.5 rounded-full border", settlementStatusColors[s.status])}>{s.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">Driver Qualification File</p>
                <span className="text-xs text-muted-foreground">{dqfDone} of {DQF_ITEMS.length} complete</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                {DQF_ITEMS.map(item => {
                  const done = !!driver[item.key];
                  return (
                    <div key={item.key} className="flex items-center gap-2 py-1">
                      {done
                        ? <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                        : <Circle className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" />}
                      <span className={cn("text-xs", done ? "text-foreground" : "text-muted-foreground")}>{item.label}</span>
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 gap-4 pt-3 border-t border-border">
                <ExpiryStat label="CDL Expiration" date={driver.cdl_expiration_date} />
                <ExpiryStat label="Medical Card Expiration" date={driver.med_card_expiration_date} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}